// @flow
import gql from 'graphql-tag';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/react-hooks';
import jwtDecode from 'jwt-decode';
import moment from 'moment';

import { REFRESH_TOKEN_SUCCESS, USER_INACTIVE, TIMEOUT_MODAL_DISMISSED } from '../containers/SignIn/constants';
import { isActiveSince, lastActivitySet } from '../utils/activity';
import { getAuthSyncChannel } from '../utils/tabSync';
import { acquireRefreshLock, isRefreshLocked } from '../utils/authLock';
import { authGet, hasActiveSession } from '../utils/authentication';
import { authTokenSelector } from '../containers/SignIn/selectors';

const REFRESH_TOKEN = gql`
  mutation refreshToken {
    refreshToken {
      token
      expiresIn
    }
  }
`;

const LOGOUT = gql`
  mutation logout {
    logout {
      success
      message
    }
  }
`;

const refreshTokenSuccess = (auth: { token: string, expiresIn: number }) => ({
  type: REFRESH_TOKEN_SUCCESS,
  auth,
});

const userInactive = () => ({
  type: USER_INACTIVE,
});

const getRawToken = () => authGet().get('token');

const useSessionTimeout = () => {
  const dispatch = useDispatch();
  const sessionToken = useSelector(authTokenSelector);

  const [timeoutModalProps, setTimeoutModalProps] = useState(null);

  const timeoutModalPropsRef = useRef(timeoutModalProps);
  const authSyncChannelRef = useRef(getAuthSyncChannel());

  // Prevent sensitive data exposure in cache.
  const [refreshToken] = useMutation(REFRESH_TOKEN, { fetchPolicy: 'no-cache' });
  const [logout] = useMutation(LOGOUT);

  const handleRefreshToken = async () => {
    if (!(await acquireRefreshLock())) {
      return;
    }

    try {
      const {
        data: { refreshToken: token },
      } = await refreshToken();
      dispatch(refreshTokenSuccess(token));
    } finally {
      setTimeoutModalProps(null);
      authSyncChannelRef.current.postMessage(TIMEOUT_MODAL_DISMISSED);
    }
  };

  const deleteSession = () => {
    dispatch(userInactive());
    setTimeoutModalProps(null);
    authSyncChannelRef.current.postMessage(TIMEOUT_MODAL_DISMISSED);
  };

  const onConfirmRefreshToken = async () => {
    lastActivitySet();
    setTimeoutModalProps(null);
    await handleRefreshToken();
  };

  const onCancelRefreshToken = async () => {
    try {
      await logout();
    } finally {
      deleteSession();
    }
  };

  const getSessionState = useCallback(() => {
    const token = getRawToken();
    const warningSeconds = parseInt(process.env.REACT_APP_TIMEOUT_WARNING_SECONDS || 120, 10);
    const decodedToken = jwtDecode(token);

    const now = moment();
    const tokenExpiry = moment.unix(decodedToken.exp);
    const warningThreshold = moment.unix(decodedToken.exp).subtract(warningSeconds, 'seconds');

    const exceedsThreshold = now.isAfter(warningThreshold);
    const isExpired = now.isAfter(tokenExpiry);
    const isUserActive = isActiveSince(warningSeconds * 1000);

    return {
      shouldAutoRefresh: exceedsThreshold && isUserActive,
      shouldShowModal: exceedsThreshold && !isUserActive,
      shouldLogout: isExpired,
    };
  }, []);

  const checkSessionStatus = useCallback(async () => {
    if (!getRawToken()) {
      if (sessionToken) deleteSession();
      if (timeoutModalPropsRef.current) setTimeoutModalProps(null);
      return null;
    }
    if (!hasActiveSession()) {
      return deleteSession();
    }
    const sessionState = getSessionState();

    if (sessionState.shouldLogout) {
      return deleteSession();
    }

    if (sessionState.shouldAutoRefresh && !isRefreshLocked()) {
      return handleRefreshToken();
    }

    const currentModalProps = timeoutModalPropsRef.current;

    if (sessionState.shouldShowModal && !currentModalProps) {
      return setTimeoutModalProps({
        open: true,
        title: 'app.session.inactivity.title',
        message: 'app.session.inactivity.message',
        confirmLabel: 'app.session.inactivity.yes',
        cancelLabel: 'app.session.inactivity.no',
        onConfirm: onConfirmRefreshToken,
        onCancel: onCancelRefreshToken,
      });
    }

    if (!sessionState.shouldShowModal && currentModalProps) {
      return setTimeoutModalProps(null);
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSessionState, sessionToken]);

  useEffect(() => {
    timeoutModalPropsRef.current = timeoutModalProps;
  }, [timeoutModalProps]);

  useEffect(() => {
    const unsubscribe = authSyncChannelRef.current.subscribe(TIMEOUT_MODAL_DISMISSED, () => {
      setTimeoutModalProps(null);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    checkSessionStatus();
    const interval = setInterval(() => {
      checkSessionStatus();
    }, 5000);

    return () => clearInterval(interval);
  }, [checkSessionStatus]);

  return { timeoutModalProps };
};

export default useSessionTimeout;
